import { useEffect, useState } from "react";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import MenuIcon from "@mui/icons-material/Menu";
import dojoLogo from "images/blue-text.svg";
import { navLinks } from "../data";
import { Button, resolveAssetUrl } from "../shared";
import { MobileMenu } from "./MobileMenu";

/**
 * Glassmorphic sticky header with logo, nav, login and sign up CTAs.
 * Animates padding/opacity on scroll.
 *
 * @returns {import("react").ReactElement}
 */
export function StickyHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const wrapPadding = scrolled ? "py-2" : "py-3.5";
  const headerPadding = scrolled ? "py-2 px-5" : "py-3 px-6";
  const headerBg = scrolled ? "bg-white/92 shadow-card" : "bg-white/75 shadow-soft";

  return (
    <>
      <div
        className={`sticky top-0 z-[100] px-4 md:px-6 ${wrapPadding} transition-[padding] duration-300`}
      >
        <header
          className={`border-line mx-auto flex max-w-[1240px] items-center justify-between rounded-full border backdrop-blur-xl transition-all duration-300 ${headerPadding} ${headerBg}`}
        >
          <a href="/home#top" className="flex items-center no-underline">
            <img src={resolveAssetUrl(dojoLogo)} alt="DojoMojo" className="h-7 w-auto md:h-8" />
          </a>

          <nav className="hidden items-center gap-0.5 md:flex">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-ink-soft hover:text-cerulean hover:bg-ice relative rounded-full px-4 py-2 text-sm font-medium transition"
              >
                {link.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <a
              href="/app/login"
              className="text-ink-soft hover:text-cerulean hover:bg-ice hidden rounded-full px-4 py-2 text-sm font-medium transition md:inline-flex"
            >
              Log In
            </a>
            <Button
              variant="signup"
              href="/app/apply"
              className="px-4! whitespace-nowrap md:px-5!"
              icon={<ArrowForwardIcon fontSize="inherit" sx={{ fontSize: 16 }} />}
            >
              <span className="md:hidden">Apply</span>
              <span className="hidden md:inline">Apply to Join</span>
            </Button>
            <button
              type="button"
              aria-label="Open menu"
              onClick={() => setMenuOpen(true)}
              className="text-ink hover:bg-ice inline-flex h-10 w-10 items-center justify-center rounded-full transition md:hidden"
            >
              <MenuIcon fontSize="small" />
            </button>
          </div>
        </header>
      </div>
      <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
    </>
  );
}
